import type { Plugin, ResolvedConfig, ViteDevServer } from "vite";
import { createJsxTaggerFeature } from "./features/jsxSource";
import { createTailwindConfigFeature } from "./features/tailwindConfig";

export interface VibeTaggerOptions {
  /** Enable JSX source tagging. Default: true */
  jsxSource?: boolean;
  /**
   * Emit a resolved Tailwind theme JSON to `src/tailwind.config.vibe.json`
   * on build start and on config-file changes. Supports Tailwind v3
   * (reads `tailwind.config.ts`) and v4 (reads `src/styles.css` etc.).
   * Default: false
   */
  tailwindConfig?: boolean;
  /** Enable debug logging. Default: false */
  debug?: boolean;
}

interface Feature {
  resolveId?(id: string, importer?: string): string | null;
  load?(id: string): string | null;
  onConfigResolved?(config: ResolvedConfig): void;
  onBuildStart?(): void | Promise<void>;
  onConfigureServer?(server: ViteDevServer): void;
}

export function vibeTagger({
  jsxSource = true,
  tailwindConfig = false,
  debug: _debug = false,
}: VibeTaggerOptions = {}): Plugin {
  const features: Feature[] = [];

  if (jsxSource) features.push(createJsxTaggerFeature());
  if (tailwindConfig) features.push(createTailwindConfigFeature());

  const plugin: Plugin = {
    name: "vibe-tagger",
    enforce: "pre",

    configResolved(config) {
      for (const feature of features) feature.onConfigResolved?.(config);
    },

    async buildStart() {
      for (const feature of features) await feature.onBuildStart?.();
    },

    configureServer(server) {
      for (const feature of features) feature.onConfigureServer?.(server);
    },

    resolveId(id: string, importer?: string) {
      for (const feature of features) {
        const result = feature.resolveId?.(id, importer);
        if (result !== null && result !== undefined) return result;
      }
      return null;
    },

    load(id: string) {
      for (const feature of features) {
        const result = feature.load?.(id);
        if (result !== null && result !== undefined) return result;
      }
      return null;
    },
  };

  return plugin;
}
